import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Plus, Play, Settings as SettingsIcon } from 'lucide-react';
import { getFullState } from '../../storage/persistence';
import { aggregateActivity, computeStreak, monthRange } from '../../logic/activity';
import { parseDSL } from '../../logic/parser';
import { resolvePatternColor } from '../../logic/patternColor';
import { resolveSessionPattern } from '../../logic/sessionPattern';
import { calculateIRState } from '../../logic/engine.ir';
import { Logo } from '../Logo';
import { PrimaryButton, SuccessButton, Touchable } from '../touchable';
import { MonthGrid } from '../ActivityCalendar/MonthGrid';
import { NewProjectWizard } from '../NewProjectWizard';
import { LazyDayDetailSheet } from '../../lazyRoutes';
import { StatsStrip } from './StatsStrip';
import { ResumeCard } from './ResumeCard';
import { HomeEmptyState } from './HomeEmptyState';
import type { DisplaySession } from '../SessionCard';
import type { KnittingState } from '../../types/state';

export const HomePage: React.FC = () => {
  const { t } = useTranslation('session');
  const navigate = useNavigate();
  const [state, setState] = useState<KnittingState | null>(null);
  // Captured once at mount; goes stale past midnight (same caveat the
  // calendar page carried).
  const now = useMemo(() => new Date(), []);
  const [view, setView] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getFullState().then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const sessions = useMemo(() => state?.sessions ?? {}, [state]);
  const patterns = useMemo(() => state?.patterns ?? {}, [state]);
  const sessionCount = Object.keys(sessions).length;

  const activity = useMemo(
    () => aggregateActivity(Object.values(sessions), now),
    [sessions, now],
  );
  const streak = useMemo(() => computeStreak(activity, now), [activity, now]);
  const range = useMemo(() => monthRange(activity), [activity]);

  const patternColors = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of Object.values(patterns)) m.set(p.id, resolvePatternColor(p));
    return m;
  }, [patterns]);

  const sessionTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of Object.values(sessions)) m.set(s.id, s.title);
    return m;
  }, [sessions]);

  const sessionStatuses = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of Object.values(sessions)) m.set(s.id, s.status);
    return m;
  }, [sessions]);

  const { monthTotalMs, monthDaysKnit } = useMemo(() => {
    let total = 0;
    let days = 0;
    const prefix = `${view.year.toString().padStart(4, '0')}-${(view.month + 1)
      .toString()
      .padStart(2, '0')}-`;
    for (const [key, day] of activity) {
      if (key.startsWith(prefix) && day.totalMs > 0) {
        total += day.totalMs;
        days += 1;
      }
    }
    return { monthTotalMs: total, monthDaysKnit: days };
  }, [activity, view]);

  // Most-recently-updated non-archived, non-completed session.
  const resume = useMemo<DisplaySession | null>(() => {
    const candidates = Object.values(sessions)
      .filter((s) => s.status !== 'archived' && s.status !== 'completed')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    for (const session of candidates) {
      const pattern = patterns[session.patternId];
      if (!pattern) continue;
      let ir = resolveSessionPattern(pattern, session);
      if (!ir) {
        // pattern.structure may not be hydrated yet; try DSL as fallback.
        try {
          ir = parseDSL(pattern.dsl);
        } catch {
          // DSL unparseable — still show the card with unknown progress.
        }
      }
      const derived = ir ? calculateIRState(ir, session.history) : null;
      const totalRows = derived?.totalRows ?? 0;
      const currentRow = derived?.currentRow ?? 0;
      return {
        id: session.id,
        title: session.title || pattern.title,
        progress: totalRows > 0 ? (currentRow / totalRows) * 100 : 0,
        currentRow,
        totalRows,
        updatedAt: new Date(session.updatedAt),
        archived: false,
        color: resolvePatternColor(pattern),
        ...(session.metadata ? { metadata: session.metadata } : {}),
      };
    }
    return null;
  }, [sessions, patterns]);

  const projectCount = useMemo(
    () => Object.values(sessions).filter((s) => s.status !== 'archived').length,
    [sessions],
  );

  const selectedDay = selectedKey ? activity.get(selectedKey) ?? null : null;
  const hasLifetimeActivity = activity.size > 0;

  const header = (
    <header className="flex justify-between items-center gap-3 mb-4">
      <h1 className="m-0">
        <Logo className="text-2xl" />
      </h1>
      <div className="flex items-center gap-2">
        <Link
          to="/learn/"
          aria-label={t('home.nav.learn', 'Learn')}
          className="w-10 h-10 bg-soft rounded-full flex items-center justify-center hover:bg-line transition-colors"
        >
          <BookOpen className="w-5 h-5 text-ink-faint" />
        </Link>
        <Link
          to="/settings/"
          aria-label={t('home.nav.settings', 'Settings')}
          className="w-10 h-10 bg-soft rounded-full flex items-center justify-center hover:bg-line transition-colors"
        >
          <SettingsIcon className="w-5 h-5 text-ink-faint" />
        </Link>
      </div>
    </header>
  );

  // Hold the first paint until state loads so we flash neither the empty
  // takeover (for a returning user) nor the bare populated shell.
  if (state === null) return null;

  // Empty takeover: no sessions at all.
  if (sessionCount === 0) {
    return (
      <main className="w-full max-w-md mx-auto p-6">
        {header}
        <HomeEmptyState
          onNewProject={() => setCreating(true)}
          onJustKnit={() => navigate('/just-knit/')}
        />
        {creating && (
          <NewProjectWizard
            onCancel={() => setCreating(false)}
            onProjectCreated={(sessionId) => navigate(`/tracker/${sessionId}`)}
          />
        )}
      </main>
    );
  }

  return (
    <main className="w-full max-w-md mx-auto p-6 space-y-4">
      {header}

      <StatsStrip
        streakDays={streak.currentDays}
        todayActive={streak.todayActive}
        monthTotalMs={monthTotalMs}
        monthDaysKnit={monthDaysKnit}
        muted={!hasLifetimeActivity}
      />

      <MonthGrid
        viewYear={view.year}
        viewMonth={view.month}
        activity={activity}
        patternColors={patternColors}
        today={now}
        earliestKey={range.earliest}
        selectedKey={selectedKey ?? ''}
        onSelectDay={setSelectedKey}
        onPrevMonth={() =>
          setView((v) => {
            const d = new Date(v.year, v.month - 1, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
          })
        }
        onNextMonth={() =>
          setView((v) => {
            const d = new Date(v.year, v.month + 1, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
          })
        }
      />

      <div className="border-t border-line pt-4 space-y-3">
        {resume && (
          <>
            <span className="block text-[10px] uppercase tracking-widest text-ink-faint">
              {t('home.resume.label', 'Pick up where you left off')}
            </span>
            <ResumeCard session={resume} now={now} onResume={(id) => navigate(`/tracker/${id}`)} />
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <SuccessButton
            onClick={() => navigate('/just-knit/')}
            className="py-4 rounded-2xl font-extrabold flex items-center justify-center gap-2"
          >
            <Play className="w-5 h-5" fill="currentColor" />
            {t('home.actions.justKnit', 'JUST KNIT')}
          </SuccessButton>
          <PrimaryButton
            onClick={() => setCreating(true)}
            className="py-4 rounded-2xl font-extrabold flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" strokeWidth={3} />
            {t('home.actions.newProject', 'NEW PROJECT')}
          </PrimaryButton>
        </div>

        <div className="text-center">
          <Touchable
            haptic="softTap"
            onClick={() => navigate('/projects/')}
            className="text-sm font-bold text-ink-faint hover:text-ink"
          >
            {t('home.allProjects', 'All projects ({{count}}) →', { count: projectCount })}
          </Touchable>
        </div>
      </div>

      {selectedKey && (
        <Suspense fallback={null}>
          <LazyDayDetailSheet
            dayKey={selectedKey}
            day={selectedDay}
            sessionTitles={sessionTitles}
            patternColors={patternColors}
            sessionStatuses={sessionStatuses}
            onResume={(id) => navigate(`/tracker/${id}`)}
            onClose={() => setSelectedKey(null)}
          />
        </Suspense>
      )}

      {creating && (
        <NewProjectWizard
          onCancel={() => setCreating(false)}
          onProjectCreated={(sessionId) => navigate(`/tracker/${sessionId}`)}
        />
      )}
    </main>
  );
};
